// Playback of the consultant's voice, and the level signal it drives the blob
// with while speaking.

import { LevelMeter, PLAYBACK_LEVEL_OPTIONS, unlockAudio } from "./level";

// A zero-sample WAV. Playing it inside a user gesture marks the element as
// user-activated on iOS, which is what lets a later reply — arriving from the
// network, long after the tap — start playing on its own.
const SILENT_CLIP =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgD4AAAB9AAACABAAZGF0YQAAAAA=";

export class SpeechPlayer {
  /** Fires when a reply finishes, or when it could not be played at all. */
  onended: (() => void) | null = null;

  /**
   * Fires INSTEAD of `onended` when playback failed, for the one caller that has
   * a second way to fetch the same audio: the streaming GET src falls back to the
   * buffered POST path. Left null, failure keeps reporting through `onended` and
   * nothing about the old behaviour changes.
   */
  onerror: (() => void) | null = null;

  private el: HTMLAudioElement | null = null;
  private source: MediaElementAudioSourceNode | null = null;
  private meter: LevelMeter | null = null;
  private speaking = false;
  private primed = false;
  // Every play() takes a ticket; late events from a superseded clip are ignored.
  private ticket = 0;

  /** 0..1, smoothed. Zero the instant playback stops. */
  get level(): number {
    return this.meter?.level ?? 0;
  }

  get playing(): boolean {
    return this.speaking;
  }

  /**
   * Call from the first user interaction. Resumes the AudioContext and buys the
   * element permission to play later without a gesture.
   */
  async unlock(): Promise<void> {
    const ctx = await unlockAudio();
    const el = this.element();
    if (ctx) this.route(ctx, el);
    if (this.primed) return;
    this.primed = true;
    try {
      el.src = SILENT_CLIP;
      await el.play();
      el.pause();
    } catch {
      // Some browsers reject a zero-length clip. Harmless: the context resume
      // above is what actually matters everywhere except iOS.
    }
  }

  /**
   * Plays a reply. Resolves once audio has started (or has failed to), not when
   * it ends — await `onended` for that. A second call cancels the first without
   * firing `onended` for it, so the UI does not flip back to idle mid-sentence.
   *
   * Never rejects: this is a conversation, and a voice that will not play must
   * cost the person nothing more than silence.
   */
  async play(url: string): Promise<void> {
    const mine = ++this.ticket;
    const el = this.element();
    this.halt();

    const ctx = await unlockAudio();
    if (mine !== this.ticket) return;
    if (ctx) this.route(ctx, el);

    this.speaking = true;
    this.meter?.reset();
    this.meter?.start();
    el.src = url;
    try {
      await el.play();
    } catch (err) {
      if (mine !== this.ticket) return;
      this.fail(err);
    }
  }

  /** Stops immediately and drops the level so the blob settles on this frame. */
  stop(): void {
    this.ticket++;
    this.halt();
  }

  /** Releases the element and the analyser. The AudioContext is shared, so it stays. */
  destroy(): void {
    this.stop();
    this.meter?.dispose();
    this.meter = null;
    this.source?.disconnect();
    this.source = null;
    if (this.el) {
      this.el.removeAttribute("src");
      this.el.load();
      this.el = null;
    }
    this.onended = null;
    this.onerror = null;
  }

  private element(): HTMLAudioElement {
    if (this.el) return this.el;
    const el = new Audio();
    el.preload = "auto";
    // Our /api/voice/speak is same-origin, but if it ever moves to a CDN an
    // unmarked cross-origin element makes the analyser read pure zeros while
    // the audio still plays — a blob that sits still and nothing in the console.
    el.crossOrigin = "anonymous";
    el.addEventListener("ended", () => {
      if (!this.speaking) return;
      this.halt();
      this.onended?.();
    });
    el.addEventListener("error", () => {
      if (!this.speaking) return;
      this.fail(el.error);
    });
    this.el = el;
    return el;
  }

  /**
   * createMediaElementSource re-routes the element into the graph permanently,
   * so it is only safe once the context is running — routing into a suspended
   * context plays the reply silently, with no error anywhere. Until then the
   * element plays on its own and the blob simply gets no level.
   */
  private route(ctx: AudioContext, el: HTMLAudioElement): void {
    if (this.source || ctx.state !== "running") return;
    try {
      const source = ctx.createMediaElementSource(el);
      this.meter = new LevelMeter(ctx, source, PLAYBACK_LEVEL_OPTIONS);
      source.connect(ctx.destination);
      this.source = source;
    } catch (err) {
      console.warn("speech level meter unavailable", err);
      this.source = null;
      this.meter = null;
    }
  }

  private fail(err: unknown): void {
    console.warn("speech playback failed", err);
    this.halt();
    const report = this.onerror ?? this.onended;
    report?.();
  }

  private halt(): void {
    this.speaking = false;
    this.meter?.stop();
    this.meter?.reset();
    if (!this.el) return;
    try {
      this.el.pause();
      this.el.currentTime = 0;
    } catch {
      // pause() on an element with no loaded source throws in some browsers.
    }
  }
}
